import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { 
  Cog6ToothIcon,
  GlobeAltIcon,
  UserGroupIcon,
  AcademicCapIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';

const AdminSettingsPage = () => {
  const [settings, setSettings] = useState({
    siteTitle: '',
    contactEmail: '',
    maintenanceMode: false,
    allowRegistrations: true,
    defaultUserRole: 'user',
    enablePrerequisites: true,
    defaultCertificateTemplate: 'template1',
    siteLogoUrl: '',
    whatsappGroupUrl: '',
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      setIsLoading(true);
      try {
        const response = await api.get('/admin/settings');
        if (response.data && response.data.status === 'success' && response.data.data.settings) {
          const fetchedSettings = response.data.data.settings;
          setSettings({
            ...fetchedSettings,
            siteTitle: fetchedSettings.siteName || '',
          });
        } else {
          console.warn('Could not fetch settings or settings are not initialized:', response.data.message);
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    setSettings(prevSettings => ({
      ...prevSettings,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSaveChanges = async () => {
    setIsSaving(true);
    setSaveStatus(null);
    
    const payload = {
      ...settings,
      siteName: settings.siteTitle,
    };
    delete payload.siteTitle;

    try {
      const response = await api.put('/admin/settings', payload);
      if (response.data && response.data.status === 'success') {
        const updatedSettings = response.data.data.settings;
        setSettings({
          ...updatedSettings,
          siteTitle: updatedSettings.siteName || '',
        });
        setSaveStatus({ type: 'success', message: 'Settings saved successfully!' });
      } else {
        setSaveStatus({ type: 'error', message: response.data.message || 'Failed to save settings.' });
      }
    } catch (error) {
      console.error('Error saving settings:', error.response?.data?.message || error.message);
      setSaveStatus({ type: 'error', message: error.response?.data?.message || 'An error occurred while saving settings.' });
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      console.log('Selected site logo file:', file);
      alert('Site logo upload is not fully implemented yet. This is a placeholder.');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Memuat pengaturan...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 border border-gray-100">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mr-4">
                <Cog6ToothIcon className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Admin Settings</h1>
                <p className="text-gray-600">Kelola pengaturan sistem TERAPLUS</p>
              </div>
            </div>
          </div>
        </div>

        {/* Save Status */}
        {saveStatus && (
          <div className={`mb-6 p-4 rounded-xl border ${
            saveStatus.type === 'success' 
              ? 'bg-green-50 border-green-200' 
              : 'bg-red-50 border-red-200'
          }`}>
            <div className="flex items-center">
              {saveStatus.type === 'success' ? (
                <CheckCircleIcon className="w-5 h-5 text-green-600 mr-3" />
              ) : (
                <XCircleIcon className="w-5 h-5 text-red-600 mr-3" />
              )}
              <span className={`text-sm font-medium ${
                saveStatus.type === 'success' ? 'text-green-800' : 'text-red-800'
              }`}>
                {saveStatus.message}
              </span>
            </div>
          </div>
        )}

        {/* General Site Settings */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 mb-8 overflow-hidden">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 md:px-8 py-6 border-b border-gray-100">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mr-3">
                <GlobeAltIcon className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">General Site Settings</h2>
                <p className="text-gray-600 text-sm">Pengaturan umum website</p>
              </div>
            </div>
          </div>
          
          <div className="p-6 md:p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="siteTitle" className="block text-sm font-medium text-gray-700 mb-2">Site Title</label>
                <input
                  type="text"
                  name="siteTitle"
                  id="siteTitle"
                  value={settings.siteTitle}
                  onChange={handleInputChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm transition-colors duration-200"
                  placeholder="Enter site title"
                />
              </div>
              <div>
                <label htmlFor="contactEmail" className="block text-sm font-medium text-gray-700 mb-2">Contact Email</label>
                <input
                  type="email"
                  name="contactEmail"
                  id="contactEmail"
                  value={settings.contactEmail}
                  onChange={handleInputChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm transition-colors duration-200"
                  placeholder="contact@example.com"
                />
              </div>
              <div>
                <label htmlFor="whatsappGroupUrl" className="block text-sm font-medium text-gray-700 mb-2">WhatsApp Group URL</label>
                <input
                  type="text"
                  name="whatsappGroupUrl"
                  id="whatsappGroupUrl"
                  value={settings.whatsappGroupUrl}
                  onChange={handleInputChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm transition-colors duration-200"
                  placeholder="https://chat.whatsapp.com/..."
                />
              </div>
              <div className="md:col-span-2">
                <label htmlFor="siteLogo" className="block text-sm font-medium text-gray-700 mb-2">Site Logo</label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors duration-200">
                  <input
                    type="file"
                    name="siteLogo"
                    id="siteLogo"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <label htmlFor="siteLogo" className="cursor-pointer">
                    <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-2">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                      </svg>
                    </div>
                    <p className="text-sm text-gray-600">Click to upload logo or drag and drop</p>
                  </label>
                </div>
                {settings.siteLogoUrl && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-600 mb-2">Current logo:</p>
                    <img 
                      src={settings.siteLogoUrl.startsWith('http') ? settings.siteLogoUrl : `${api.defaults.baseURL.replace('/api', '')}${settings.siteLogoUrl}`} 
                      alt="Site Logo" 
                      className="h-16 border rounded-lg p-2 bg-white"
                    />
                  </div>
                )}
              </div>
              <div className="flex items-center p-4 bg-gray-50 rounded-lg">
                <input
                  id="maintenanceMode"
                  name="maintenanceMode"
                  type="checkbox"
                  checked={settings.maintenanceMode}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="maintenanceMode" className="ml-3 block text-sm text-gray-900">
                  Enable Maintenance Mode
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* User Management Settings */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 mb-8 overflow-hidden">
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 px-6 md:px-8 py-6 border-b border-gray-100">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full flex items-center justify-center mr-3">
                <UserGroupIcon className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">User Management Settings</h2>
                <p className="text-gray-600 text-sm">Pengaturan manajemen pengguna</p>
              </div>
            </div>
          </div>
          
          <div className="p-6 md:p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-center p-4 bg-gray-50 rounded-lg">
                <input
                  id="allowRegistrations"
                  name="allowRegistrations"
                  type="checkbox"
                  checked={settings.allowRegistrations}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="allowRegistrations" className="ml-3 block text-sm text-gray-900">
                  Allow New User Registrations
                </label>
              </div>
              <div>
                <label htmlFor="defaultUserRole" className="block text-sm font-medium text-gray-700 mb-2">Default Role for New Users</label>
                <select
                  id="defaultUserRole"
                  name="defaultUserRole"
                  value={settings.defaultUserRole}
                  onChange={handleInputChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm transition-colors duration-200"
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Course Settings */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 mb-8 overflow-hidden">
          <div className="bg-gradient-to-r from-purple-50 to-indigo-50 px-6 md:px-8 py-6 border-b border-gray-100">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-full flex items-center justify-center mr-3">
                <AcademicCapIcon className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Course Settings</h2>
                <p className="text-gray-600 text-sm">Pengaturan kursus dan sertifikat</p>
              </div>
            </div>
          </div>
          
          <div className="p-6 md:p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-center p-4 bg-gray-50 rounded-lg">
                <input
                  id="enablePrerequisites"
                  name="enablePrerequisites"
                  type="checkbox"
                  checked={settings.enablePrerequisites}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="enablePrerequisites" className="ml-3 block text-sm text-gray-900">
                  Enable Course Prerequisites Globally
                </label>
              </div>
              <div>
                <label htmlFor="defaultCertificateTemplate" className="block text-sm font-medium text-gray-700 mb-2">Default Certificate Template</label>
                <select
                  id="defaultCertificateTemplate"
                  name="defaultCertificateTemplate"
                  value={settings.defaultCertificateTemplate}
                  onChange={handleInputChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm transition-colors duration-200"
                >
                  <option value="template1">Standard Template</option>
                  <option value="template2">Modern Template</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleSaveChanges}
            disabled={isSaving}
            className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-semibold rounded-xl shadow-lg hover:from-blue-600 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
          >
            {isSaving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                Saving...
              </>
            ) : (
              <>
                <CheckCircleIcon className="w-4 h-4 mr-2" />
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminSettingsPage;
